/**
 * useSectionSumsRefetch — jedno ponowienie zapytania sekcji, które dociąga sumy „Razem" po
 * powrocie z widoku „Karty" do „Listy" (patrz opis w `DecisionSectionTable.tsx`, „Sumy «Razem»").
 *
 * Sumy są tylko w widoku „Lista" (`wantSums`, liczy go tabela: widok + kolumny kwot sekcji), a
 * klucz zapytania nie niesie prośby o nie, więc dane wczytane w „Kartach" sum nie mają
 * (`requestedSums` = `false` w odpowiedzi). Hak ponawia wtedy TO SAMO zapytanie (ten sam klucz —
 * bez danych zastępczych i bez zdejmowania zaznaczenia).
 */

import { useEffect, useRef } from 'react';

/** To, co hak czyta z wyniku zapytania sekcji (`useQuery` tabeli); przekazuj cały wynik. */
export interface SumsRefetchQuery {
  /** Odpowiedź z adnotacją, czy poproszono o sumy (`requestedSums`). */
  data: { requestedSums: boolean } | undefined;
  isPlaceholderData: boolean;
  isFetching: boolean;
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
}

export interface UseSectionSumsRefetchArgs {
  query: SumsRefetchQuery;
  /** Czy sekcja chce sum: widok „Lista" i co najmniej jedna kolumna kwot w sekcji. */
  wantSums: boolean;
  /**
   * Czy sekcja ma zaznaczone wiersze. Zaznaczenie wstrzymuje auto-odświeżanie zakładki, więc nie
   * wolno go omijać dociąganiem sum — liczy to tabela (potrzebuje tego też odświeżenie listy firm).
   */
  hasMarkedRows: boolean;
}

export function useSectionSumsRefetch({
  query,
  wantSums,
  hasMarkedRows,
}: UseSectionSumsRefetchArgs): void {
  // Dane wczytane w widoku „Karty" nie mają sum. Po powrocie do „Listy" jedno ponowienie
  // zapytania je dociąga (ten sam klucz — bez danych zastępczych i bez zdejmowania zaznaczenia).
  // Ponawiamy raz na zestaw danych (`dataUpdatedAt`) i na wejście w „Listę": nieudane ponowienie
  // nie zapętla się, a ponowne przełączenie widoku próbuje jeszcze raz. Przy zaznaczonych
  // wierszach nie ponawiamy (patrz `hasMarkedRows`) — po zdjęciu zaznaczenia efekt dociąga sumy
  // jednym ponowieniem.
  const { refetch: refetchSection } = query;
  const needsSumsRefetch =
    wantSums &&
    query.data !== undefined &&
    !query.data.requestedSums &&
    !query.isPlaceholderData &&
    !query.isFetching;
  const sumsRefetchedForRef = useRef<number | null>(null);
  const sectionDataUpdatedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (!wantSums) {
      sumsRefetchedForRef.current = null;
      return;
    }
    if (hasMarkedRows) return;
    if (!needsSumsRefetch || sumsRefetchedForRef.current === sectionDataUpdatedAt) return;
    sumsRefetchedForRef.current = sectionDataUpdatedAt;
    void refetchSection();
  }, [wantSums, needsSumsRefetch, hasMarkedRows, sectionDataUpdatedAt, refetchSection]);
}
